/**
 * ENG-3716 — "waiting-for-video-track" state machine.
 *
 * A video-source connect used to be fire-and-forget: the pipeline add committed
 * the device, and the UI then waited on a 'subscribed to video track' event that
 * was never bound to any deadline. When the subscribe never arrived (bridge
 * cannot reach the camera; LiveKit publisher churn after a reconnect; a
 * snapshot-restored device id absent from live discovery) the source badge sat
 * on "Connecting..." forever with no error path.
 *
 * This module binds every video-source connect to an explicit per-device state
 * machine:
 *
 *   begin  ──► waiting ──(track subscribed)──► subscribed
 *                 │
 *                 ├──(~15s timeout, no reconnect seen)──► failed: camera-unreachable
 *                 ├──(~15s timeout, reconnect seen)─────► failed: stream-interrupted
 *                 ├──(restore validation error)─────────► failed: device-not-discovered
 *                 └──(agent cannot stream H.265)────────► failed: h265-unsupported
 *
 * A later successful subscribe always recovers a failed entry (escape hatch in
 * both directions), and a fresh begin() supersedes any previous state.
 *
 * Deliberately pure (no logger / react / livekit imports) so the failure modes
 * are unit-testable without the renderer environment; callers own logging.
 * Per rule: think-in-systems (ai.knowledge/global) — a small policy module
 * consumed by the LiveKit integration layer and the badge UI.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — every path out
 * of 'waiting' (subscribe, timeout, explicit failure, clear) settles the timer
 * and notifies subscribers.
 */

/** How long a source may sit in 'waiting' before it is declared failed. */
export const VIDEO_TRACK_WAIT_TIMEOUT_MS = 15_000;

/** Why a video-source connect failed — keys the actionable UI copy. */
export type VideoTrackWaitFailureCause =
  // (a) the bridge never published a track — camera unreachable from the bridge.
  | 'camera-unreachable'
  // (b) LiveKit publisher churn after a room reconnect — the selection never resubscribed.
  | 'stream-interrupted'
  // (c) a snapshot-restored device id is absent from live discovery.
  | 'device-not-discovered'
  // The receiving app cannot decode the format published by the camera.
  | 'unsupported-video-format'
  // An Iris-enabled camera's agent found an H.265 feed it cannot stream.
  | 'h265-unsupported';

export type VideoTrackWaitState =
  | { phase: 'waiting'; startedAt: number; sawReconnect: boolean }
  | { phase: 'subscribed' }
  | { phase: 'failed'; cause: VideoTrackWaitFailureCause };

type Entry = {
  state: VideoTrackWaitState;
  timer: ReturnType<typeof setTimeout> | undefined;
};

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

const clearTimer = (entry: Entry | undefined): void => {
  if (entry?.timer !== undefined) {
    clearTimeout(entry.timer);
    entry.timer = undefined;
  }
};

/**
 * Subscribe to state-machine changes (useSyncExternalStore contract).
 * Returns the unsubscribe function.
 */
export const subscribeVideoTrackWait = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Current state for a device, or undefined when no connect is being tracked. */
export const getVideoTrackWaitState = (deviceId: string): VideoTrackWaitState | undefined =>
  entries.get(deviceId)?.state;

/**
 * Start (or restart) the wait for a device's video track. A fresh connect
 * attempt supersedes any previous state — including a previous failure — so a
 * user retry always gets a clean 15s window.
 */
export const beginVideoTrackWait = (
  deviceId: string,
  timeoutMs: number = VIDEO_TRACK_WAIT_TIMEOUT_MS
): void => {
  clearTimer(entries.get(deviceId));

  const entry: Entry = {
    state: { phase: 'waiting', startedAt: Date.now(), sawReconnect: false },
    timer: undefined
  };

  entry.timer = setTimeout(() => {
    entry.timer = undefined;

    // Only a still-waiting entry can time out; resolve/fail/clear already
    // settled the machine (and cleared this timer) on their own paths.
    if (entry.state.phase !== 'waiting') {
      return;
    }

    // A reconnect during the window points at publisher churn, not at the
    // camera itself — key the failure copy accordingly.
    entry.state = {
      phase: 'failed',
      cause: entry.state.sawReconnect ? 'stream-interrupted' : 'camera-unreachable'
    };
    notify();
  }, timeoutMs);

  entries.set(deviceId, entry);
  notify();
};

/**
 * The expected 'subscribed to video track' event arrived for this device.
 * Also recovers a previously-failed entry: a late subscribe is still a success.
 */
export const resolveVideoTrackWait = (deviceId: string): void => {
  const entry = entries.get(deviceId);

  // No tracked connect (e.g. steady-state resubscribes) — nothing to settle.
  if (!entry || entry.state.phase === 'subscribed') {
    return;
  }

  clearTimer(entry);
  entry.state = { phase: 'subscribed' };
  notify();
};

/**
 * Transition a device to a concrete failure. Creates the entry when none is
 * being tracked — the boot-time snapshot restore path fails without a
 * preceding begin().
 */
export const failVideoTrackWait = (deviceId: string, cause: VideoTrackWaitFailureCause): void => {
  const entry = entries.get(deviceId);

  clearTimer(entry);
  entries.set(deviceId, { state: { phase: 'failed', cause }, timer: undefined });
  notify();
};

/** Stop tracking a device entirely (device removed / room torn down). */
export const clearVideoTrackWait = (deviceId: string): void => {
  const entry = entries.get(deviceId);

  if (!entry) {
    return;
  }

  clearTimer(entry);
  entries.delete(deviceId);
  notify();
};

/**
 * A LiveKit room reconnect happened. Mark every pending wait so a subsequent
 * timeout is reported as 'stream-interrupted' (publisher churn) instead of
 * blaming the camera.
 */
export const noteReconnectForPendingVideoTrackWaits = (): void => {
  let changed = false;

  for (const entry of entries.values()) {
    if (entry.state.phase === 'waiting' && !entry.state.sawReconnect) {
      entry.state = { ...entry.state, sawReconnect: true };
      changed = true;
    }
  }

  if (changed) {
    notify();
  }
};

/** Test/reset helper: drop all tracked waits and their timers. */
export const resetVideoTrackWaits = (): void => {
  for (const entry of entries.values()) {
    clearTimer(entry);
  }
  entries.clear();
  notify();
};
